// CSV/TSV parsing for the Add objects view: paste or upload a table of type
// values for a TypeSet (e.g. a Geonorge object-catalogue export). Delimiter
// is auto-detected; splitting is quote-aware.

import type { TranslationGenerateTypeValuesMutation } from "@/v2-backoffice-client/translation/addObjects/__types__/translation.generateTypeValues.mutation"
import type { TranslationSeedTypeValuesMutation } from "@/v2-backoffice-client/translation/addObjects/__types__/translation.seedTypeValues.mutation"

// AI-drafted values for one parse (view) / one AI-generated value (preview
// row) — shared so both stay derived from the same mutation type instead of
// each independently re-deriving it from the generated GraphQL types.
export type Generated =
  TranslationGenerateTypeValuesMutation["generateTypeValues"]
export type GeneratedValue = Generated[number]
export type SeedResult =
  TranslationSeedTypeValuesMutation["seedTypeValues"][number]

const DELIMITERS = [
  { char: "\t", label: "tab" },
  { char: ";", label: "semicolon" },
  { char: ",", label: "comma" },
] as const

export type ParsedTable = {
  delimiterLabel: string
  header: string[]
  rows: string[][]
}

export type ParsedTableIssues = {
  ragged: number
  emptyCode: number
  dupes: string[]
}

function detectDelimiter(firstLine: string): (typeof DELIMITERS)[number] {
  let best: (typeof DELIMITERS)[number] = DELIMITERS[2]
  let bestCount = -1
  for (const delimiter of DELIMITERS) {
    const count = firstLine.split(delimiter.char).length - 1
    if (count > bestCount) {
      bestCount = count
      best = delimiter
    }
  }
  return best
}

// Quote-aware single-line split (handles delimiters inside "..." and "" escapes).
function splitLine(line: string, delim: string): string[] {
  const out: string[] = []
  let cur = ""
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"'
        i++
      } else if (ch === '"') {
        inQuotes = false
      } else {
        cur += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === delim) {
      out.push(cur)
      cur = ""
    } else {
      cur += ch
    }
  }
  out.push(cur)
  return out.map((cell) => cell.trim())
}

export function parseTable(
  text: string,
  hasHeader: boolean,
): ParsedTable | null {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "")
  if (lines.length === 0) return null
  const delim = detectDelimiter(lines[0])
  const grid = lines.map((line) => splitLine(line, delim.char))
  const header = hasHeader ? grid[0] : grid[0].map((_, i) => `Column ${i + 1}`)
  const rows = hasHeader ? grid.slice(1) : grid
  return { delimiterLabel: delim.label, header, rows }
}

// Verification: ragged rows, empty code cells, duplicate codes.
export function findTableIssues(
  parsed: ParsedTable,
  codeColumn: number,
): ParsedTableIssues {
  const width = parsed.header.length
  const ragged = parsed.rows.filter((row) => row.length !== width).length
  const seen = new Map<string, number>()
  let emptyCode = 0
  for (const row of parsed.rows) {
    const code = row[codeColumn] ?? ""
    if (code.trim() === "") emptyCode++
    else seen.set(code, (seen.get(code) ?? 0) + 1)
  }
  const dupes = [...seen.entries()]
    .filter(([, count]) => count > 1)
    .map(([code]) => code)
  return { ragged, emptyCode, dupes }
}
